import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationComponent } from './components/navigation/navigation.component';
import { LoaderComponent } from './components/loader/loader.component';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuthService } from './services/auth.service';
import { LoaderService } from './services/loader.service';
import { ToasterService } from './services/toaster.service';
import { ApiService } from './services/api.service';
import { AuditEventDataService } from './services/audit-event-data.service';

@Component({
  selector: 'app-root',
  imports: [CommonModule, NavigationComponent, LoaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'batchmaster-ai-app';
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private authService = inject(AuthService);
  private loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private auditEventDataService = inject(AuditEventDataService);
  private readonly _destroying$ = new Subject<void>();

  ngOnInit(): void {
    // Show loader during initialization
    this.loaderService.show('auth-init');

    this.initializeMSAL();
  }

  private initializeMSAL(): void {
    // Initialize MSAL for SSO authentication
    this.msalService.instance
      .initialize()
      .then(() => {
        // Mark MSAL as initialized BEFORE any instance API calls
        this.authService.markMsalInitialized();
        return this.msalService.instance.handleRedirectPromise();
      })
      .then((result) => {
        if (result) {
          this.msalService.instance.setActiveAccount(result.account);
          console.log('✅ Login successful:', result.account.name);
          this.recordLoginEvent();
          // Update auth state FIRST, then navigate after observables update
          this.authService.checkAuthenticationState();
        } else {
          // No redirect result - normal page load
          this.checkAndSetActiveAccount();
          this.authService.checkAuthenticationState();
          // Don't hide loader here - NavigationComponent will hide it after data loads
        }
      })
      .catch((error) => {
        console.error('❌ Error handling redirect:', error);
        this.authService.markMsalInitialized();
        this.checkAndSetActiveAccount();
        this.authService.checkAuthenticationState();
        this.loaderService.hide('auth-init');
      });

    // Also listen for future state changes (e.g., after login redirect)
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.authService.checkAuthenticationState();
      });
  }

  private recordLoginEvent(): void {
    const eventData = this.auditEventDataService.getLoginEventData();
    this.apiService.recordAuthEvent('login', eventData).subscribe();
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const allAccounts = this.msalService.instance.getAllAccounts();

    console.log('🔐 Active account:', activeAccount?.name);

    if (!activeAccount && allAccounts.length > 0) {
      this.msalService.instance.setActiveAccount(allAccounts[0]);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
